export const BROWSER_INSTALL_ID_KEY = "backtest.web.installID.v1";

const FALLBACK_PREFIX = "web";
let inMemoryInstallID: string | null = null;

export interface InstallIDStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function isValidInstallID(value: string | null): value is string {
  return Boolean(value && /^[A-Za-z0-9._:-]{8,128}$/.test(value));
}

function randomInstallID(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return `${FALLBACK_PREFIX}-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
  }
  return `${FALLBACK_PREFIX}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Returns one stable, non-account browser installation identifier. It is used
 * only to keep anonymous quota isolated from other browsers. Account identity
 * remains authoritative whenever a valid bearer token is present.
 */
export function browserInstallID(
  storage: InstallIDStorage | null = typeof window !== "undefined" ? window.localStorage : null,
): string {
  if (inMemoryInstallID) return inMemoryInstallID;

  try {
    const stored = storage?.getItem(BROWSER_INSTALL_ID_KEY) ?? null;
    if (isValidInstallID(stored)) {
      inMemoryInstallID = stored;
      return stored;
    }
  } catch {
    // Private browsing or a hardened browser may make localStorage unavailable.
  }

  const generated = randomInstallID();
  inMemoryInstallID = generated;
  try {
    storage?.setItem(BROWSER_INSTALL_ID_KEY, generated);
  } catch {
    // The in-memory ID still prevents all requests in this tab sharing the
    // backend's generic anonymous fallback.
  }
  return generated;
}

export function resetBrowserInstallIDForTests(): void {
  inMemoryInstallID = null;
}
